import "../controllers/material-controllers";
import DomainModel from '../domain-model';
import ReadySignal, { hasOnReady, OnReady } from '../control-flow/ready-signal';
import logging from '../logging';
import mfac from '../material-factory';
import { Box3, Material as ThreeMaterial, Mesh, Object3D, Vector3 as ThreeVector3 } from 'three';
import { Material } from '../domain';
import { OrderedEffects } from '../control-flow/ordered-effects';
import { Vector3 } from '../math/vector3';
import { sortMaterialsBySlot } from '../controllers/material-controllers';

const log = logging('view');

export interface DomainModel3D extends DomainModel {
    position?: Vector3;
    rotation?: Vector3;
    scale?: Vector3;
    opacity?: number;
};

export interface Position extends DomainModel {
    position: Vector3;
}

export const hasPosition = (model: DomainModel): model is Position => {
    return model.hasField("position");
}

export interface Rotation extends DomainModel {
    rotation: Vector3;
}

export const hasRotation = (model: DomainModel): model is Rotation => {
    return model.hasField("rotation");
}

export interface Scale extends DomainModel {
    scale: Vector3;
}

export const hasScale = (model: DomainModel): model is Scale => {
    return model.hasField("scale");
}

export interface Opacity extends DomainModel {
    opacity: number;
}

export const hasOpacity = (model: DomainModel): model is Opacity => {
    return model.hasField("opacity");
}

export default abstract class BaseView extends Object3D implements OnReady {

    model: DomainModel3D;
    highlights: Set<Mesh> = new Set();

    protected updates: OrderedEffects = new OrderedEffects();

    onReady = new ReadySignal('onReady');

    constructor(model: DomainModel3D) {
        super();

        this.model = model;

        // If updates fail, signal readiness so that the scene doesn't stall
        // just because one thing failed.
        this.updates.onError.add(this.onReady.trigger);
        this.updates.onError.add(log.error);
    }

    /**
     * Every implementing class should ensure that the init() method runs at
     * the end of the constructor or at least some time before the view is
     * needed in the scene.
     */
    init() {
        this.make();
        this.bind();
        this.bindTransform();
    }


    /**
     * Implementations should create an empty skeleton of their 3D children.
     */
    protected abstract make(): void;

    /**
     * Implementations should bind their model's change events to their
     * particular properties.
     */
    protected abstract bind(): void;

    /**
     * An automatic side-effect of the constructor is that this Object3D
     * instance (and its subclasses) will automatically track the 3D transform
     * of their corresponding models.
     */
    protected bindTransform(): void {
        if(hasPosition(this.model)) {
            this.model.position.bind('x',
                          () => this.position.x = this.model.position.x);
            this.model.position.bind('y',
                          () => this.position.y = this.model.position.y);
            this.model.position.bind('z',
                          () => this.position.z = this.model.position.z);
        }

        if(hasRotation(this.model)) {
            this.model.rotation.bind('x',
                          () => this.rotation.x = this.model.rotation.x);
            this.model.rotation.bind('y',
                          () => this.rotation.y = this.model.rotation.y);
            this.model.rotation.bind('z',
                          () => this.rotation.z = this.model.rotation.z);
        }

        if(hasScale(this.model)) {
            this.model.scale.bind('x',
                          () => this.scale.x = this.model.scale.x);
            this.model.scale.bind('y',
                          () => this.scale.y = this.model.scale.y);
            this.model.scale.bind('z',
                          () => this.scale.z = this.model.scale.z);
        }
    }

    protected bindMaterial(mesh: Mesh, material: Material) {
        material.bind('change', () => {
            mesh.material = mfac.makefrom(material);
        });

        if(hasOpacity(this.model)) {
            this.model.bind('opacity', () => {
                const opacity = this.model.get('opacity') as number;
                mesh.material = mfac.makeTransparent(material, opacity);
                mesh.visible = opacity != 0;
            });
        }
    }

    /**
     * When a Mesh instance's property needs to be an array of materials, the
     * plain bindMaterial method won't work. This method takes care of that
     * case.
     */
    protected bindMaterialArray(mesh: Mesh, materials: Material[], slots?: string[]) {
        // If mesh.material isn't already an array, then make it into one.
        if(!Array.isArray(mesh.material)) mesh.material = new Array(materials.length);

        // If we haven't been provided slots, we get their names from the
        // material. This is the case for OBJ models, which take their
        // default material names from Blender, even when no .mtl file has
        // been loaded.
        if(!slots) slots = mesh.material.map(m => m.name);

        const sorted = sortMaterialsBySlot(slots, materials);

        for(let i=0; i < mesh.material.length; i++) {
            if(sorted[i]) {
                sorted[i].bind('change', () => {
                    mesh.material[i] = mfac.makefrom(sorted[i]);
                });
                if('opacity' in this.model) {
                    this.model.bind('opacity', () => {
                        const opacity = this.model.get('opacity') as number;
                        mesh.material[i] = mfac.makeTransparent(sorted[i], opacity);
                    });
                }
            }
        }
    }

    /**
     * Given a root object, e.g. a model loaded from a GLB file, pair the child
     * objects with the given materials by their material slots. This assumes
     * that the individual bound materials may change, but not that the
     * materials array itself will change, and that each child Mesh has a
     * single material (not a material array).
     */
    protected bindMaterialTree(root: Object3D, materials: Material[]) {
        const meshes: Mesh[] = [];
        root.traverse(child => {
            if(child instanceof Mesh && child.geometry) {
                child.castShadow = true;
                child.receiveShadow = true;
                meshes.push(child);
            }
        });

        const
            slots = meshes.map(m => (m.material as ThreeMaterial).name),
            sorted = sortMaterialsBySlot(slots, materials);

        for(let i = 0; i < sorted.length; i++) {
            if(sorted[i]) {
                sorted[i].bind('change', () => {
                    meshes[i].material = mfac.makefrom(sorted[i]);
                });
            }
        }
    }

    /**
     * Set the opacity of a tree of objects, hiding totally transparent objects
     * completely and removing fully opaque objects from the transparency pass.
     */
    protected setTreeOpacity(root: Object3D, opacity: number) {
        root.traverse(obj => {
            if(obj instanceof Mesh) {
                // Only transparent if the opacity is less than one
                obj.material.transparent = opacity < 1;
                obj.material.opacity = opacity;
                obj.visible = opacity != 0;
            }
        });
    }

    /**
     * This has the wrong return signature to satisfy a quirk of TypeScript.
     * At the moment I don't need the clone to be completely the same as its
     * source, with its attachment to a model.
     */
    public clone(deep?: boolean): this {
        // In TypeScript, this.constructor is a Function, but we want it to be
        // a constructor. Not sure how to do it any better than this.
        let clone = new Object3D();
        clone.copy(this, deep);
        return clone as this;
    }

//    /**
//     * This is an imperfect implementation and may need to be implemented by
//     * individual views. This works for render exports, but little else.
//     */
//    clone(recursive: boolean = false) {
//        const clone = new (
//            this.constructor as new(model: DomainModel) => this)(this.model);
//        clone.copy(this, recursive);
//        return clone;
//    }

    /**
     * Computes the axis-aligned bounding box that precisely contains this
     * view.
     */
    public getVisualDimensons(): ThreeVector3 {
        const box = new Box3();
        box.setFromObject(this, true);
        return box.getSize(new ThreeVector3());
    }

    add(...children: Object3D[]): this {
        super.add(...children);
        for(const child of children) {
            if(hasOnReady(child)) {
                this.onReady.depends(child.onReady);
            }
        }
        return this;
    }
}
